import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { forkJoin, Subscription, take } from 'rxjs';
import { EChartsOption } from 'echarts';
import { NgxEchartsDirective } from 'ngx-echarts';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { ThemeService } from '@/app/services/theme/theme.service';
import { StatsPeriodSelection } from '@/app/pages/admin-options/settings/molecules/admin-stats-aggregate/admin-stats-aggregate.types';
import { getKpiTrend, kpiAccentDotClass } from '@/app/pages/admin-options/settings/molecules/admin-stats-dashboard/admin-stats-dashboard.helpers';
import { formatStatsPeriodLabel, getYesterdayUtc, resolvePreviousStatsPeriod } from '@/app/pages/admin-options/settings/molecules/admin-stats-period/admin-stats-period.helpers';
import { AdminStatsPeriodFilter } from '@/app/pages/admin-options/settings/molecules/admin-stats-period-filter/admin-stats-period-filter';
import { AdminGoogleAnalyticsService } from '../../../services/admin-google-analytics.service';
import {
  buildAccountModeKpiCards,
  buildAccountModePieOption,
  buildLocationCountryCards,
  buildPlatformKpiCards,
  buildPlatformPieOption,
  platformKpiAccentDotClass,
  readGaErrorMessage,
  topLocationCities,
} from '../account-mode.helpers';
import {
  GaAccountModeKpiCard,
  GaAccountModeReport,
  GaLocationCountryCard,
  GaLocationReport,
  GaLocationRow,
  GaPlatformKpiCard,
  GaPlatformReport,
} from '../account-mode.types';

type GaDashboardPayload = {
  current: GaAccountModeReport;
  previous: GaAccountModeReport;
  platform: GaPlatformReport;
  previousPlatform: GaPlatformReport;
  locations: GaLocationReport;
};

@Component({
  selector: 'admin-google-analytics-dashboard',
  imports: [AdminStatsPeriodFilter, LoadingInlineComponent, NgxEchartsDirective],
  templateUrl: './account-mode-dashboard.html',
  styleUrl: './account-mode-dashboard.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountModeDashboard implements OnDestroy {
  readonly report = signal<GaAccountModeReport | null>(null);
  readonly kpiCards = signal<GaAccountModeKpiCard[]>([]);
  readonly platformKpiCards = signal<GaPlatformKpiCard[]>([]);
  private readonly platformReport = signal<GaPlatformReport | null>(null);
  private readonly themeService = inject(ThemeService);

  readonly pieOption = computed<EChartsOption | null>(() => {
    const current = this.report();
    return current
      ? buildAccountModePieOption(current, this.themeService.isDarkMode()).option
      : null;
  });
  readonly pieHasData = computed(
    () => buildAccountModePieOption(this.report(), this.themeService.isDarkMode()).hasData,
  );
  readonly platformPieOption = computed<EChartsOption | null>(() => {
    const platform = this.platformReport();
    return platform
      ? buildPlatformPieOption(platform, this.themeService.isDarkMode()).option
      : null;
  });
  readonly platformPieHasData = computed(
    () => buildPlatformPieOption(this.platformReport(), this.themeService.isDarkMode()).hasData,
  );
  readonly locationCountries = signal<GaLocationCountryCard[]>([]);
  readonly locationCities = signal<GaLocationRow[]>([]);
  readonly locationHasData = signal(false);
  readonly rangeLabel = signal('');
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly error = signal<string | null>(null);
  readonly latestSnapshotDate = getYesterdayUtc();

  readonly kpiAccentDotClass = kpiAccentDotClass;
  readonly platformKpiAccentDotClass = platformKpiAccentDotClass;
  readonly getKpiTrend = getKpiTrend;

  private subscriptionList = new Subscription();
  private readonly gaService = inject(AdminGoogleAnalyticsService);

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onPeriodChange(selection: StatsPeriodSelection): void {
    this.loadDashboard(selection.startDate, selection.endDate);
  }

  private loadDashboard(startDate: string, endDate: string): void {
    this.subscriptionList.unsubscribe();
    this.subscriptionList = new Subscription();
    this.loadingState.set('loading');
    this.error.set(null);

    const previous = resolvePreviousStatsPeriod(startDate, endDate);

    this.subscriptionList.add(
      forkJoin({
        current: this.gaService.getAccountMode(startDate, endDate).pipe(take(1)),
        previous: this.gaService.getAccountMode(previous.startDate, previous.endDate).pipe(take(1)),
        platform: this.gaService.getPlatform(startDate, endDate).pipe(take(1)),
        previousPlatform: this.gaService.getPlatform(previous.startDate, previous.endDate).pipe(take(1)),
        locations: this.gaService.getLocations(startDate, endDate).pipe(take(1)),
      }).pipe(take(1)).subscribe({
        next: (payload) => this.handleDashboardNext(payload),
        error: (err: unknown) => this.handleDashboardError(err),
        complete: () => this.handleDashboardComplete(),
      }),
    );
  }

  private handleDashboardNext(payload: GaDashboardPayload): void {
    try {
      this.applyDashboard(payload);
      this.loadingState.set('loaded');
      this.error.set(null);
    } catch (err) {
      this.handleDashboardError(err);
    }
  }

  private handleDashboardError(err: unknown): void {
    this.resetDashboard();
    this.loadingState.set('error');
    this.error.set(readGaErrorMessage(err, 'Unable to load Google Analytics dashboard.'));
  }

  private handleDashboardComplete(): void {
    if (this.loadingState() === 'loading' && this.report()) {
      this.loadingState.set('loaded');
    }
  }

  private applyDashboard({
    current,
    previous: previousReport,
    platform,
    previousPlatform,
    locations,
  }: GaDashboardPayload): void {
    this.report.set(current);
    this.platformReport.set(platform);
    this.kpiCards.set(buildAccountModeKpiCards(current, previousReport));
    this.platformKpiCards.set(buildPlatformKpiCards(platform, previousPlatform));
    this.locationCountries.set(buildLocationCountryCards(locations.countries));
    this.locationCities.set(topLocationCities(locations.rows));
    this.locationHasData.set(locations.countries.length > 0 || locations.rows.length > 0);
    this.rangeLabel.set(
      formatStatsPeriodLabel(current.dateRange.startDate, current.dateRange.endDate),
    );
  }

  private resetDashboard(): void {
    this.report.set(null);
    this.platformReport.set(null);
    this.kpiCards.set([]);
    this.platformKpiCards.set([]);
    this.locationCountries.set([]);
    this.locationCities.set([]);
    this.locationHasData.set(false);
  }
}
